import { ApiSimpleSubscription } from "@incendium/api";
import { Tune } from "@mui/icons-material";
import { Box, Grid, Slider, Stack, Typography } from "@mui/material";
import GlassCard from "Components/GlassCard/GlassCard";
import SubscriptionCard from "features/subscription/components/SubscriptionCard";
import {
  getSubscriptionVolumeValue,
  getVolumeMarkLabel,
  getVolumeMetricCopy,
  getVolumeSortValue,
  resolveVolumeMetric,
} from "features/subscription/utils/volumeLabels";
import { useEffect, useMemo, useState } from "react";

interface ISubscriptionVolumePickerProps {
  subscriptions: ApiSimpleSubscription[];
  currentSubscriptionId?: string;
  currentIsAnnual?: boolean;
  onSelect?: (subscription: ApiSimpleSubscription, isAnnual?: boolean) => void;
}

function SubscriptionVolumePicker({
  subscriptions,
  currentSubscriptionId,
  currentIsAnnual,
  onSelect,
}: ISubscriptionVolumePickerProps) {
  const volumeMetric = useMemo(
    () => resolveVolumeMetric(subscriptions),
    [subscriptions]
  );

  const sortedSubscriptions = useMemo(() => {
    return [...subscriptions].sort((a, b) => {
      if (volumeMetric) {
        const aVolume = getVolumeSortValue(
          getSubscriptionVolumeValue(a, volumeMetric)
        );
        const bVolume = getVolumeSortValue(
          getSubscriptionVolumeValue(b, volumeMetric)
        );
        if (aVolume !== bVolume) {
          return aVolume - bVolume;
        }
      }
      return (a.price || 0) - (b.price || 0);
    });
  }, [subscriptions, volumeMetric]);

  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (sortedSubscriptions.length === 0) {
      setSelectedIndex(0);
      return;
    }

    const currentIdx = sortedSubscriptions.findIndex(
      (s) => s.id === currentSubscriptionId
    );
    setSelectedIndex(currentIdx >= 0 ? currentIdx : 0);
  }, [sortedSubscriptions, currentSubscriptionId]);

  const selected = sortedSubscriptions[selectedIndex];
  const metricCopy = getVolumeMetricCopy(volumeMetric);

  const marks = useMemo(
    () =>
      sortedSubscriptions.map((sub, index) => ({
        value: index,
        label: getVolumeMarkLabel(
          sub,
          volumeMetric,
          sub.product?.name?.slice(0, 8) || `${index + 1}`
        ),
      })),
    [sortedSubscriptions, volumeMetric]
  );

  if (!selected || sortedSubscriptions.length === 0) {
    return null;
  }

  return (
    <GlassCard boxProps={{ p: 3 }}>
      <Grid container spacing={4} alignItems="stretch">
        <Grid item xs={12} md={5}>
          <Box sx={{ maxWidth: 420, mx: { xs: "auto", md: 0 } }}>
            <SubscriptionCard
              subscription={selected}
              dense
              onSelect={onSelect}
              isCurrent={selected.id === currentSubscriptionId}
              currentIsAnnual={
                selected.id === currentSubscriptionId
                  ? currentIsAnnual
                  : undefined
              }
              sliderCurrentSubscriptionId={currentSubscriptionId}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Stack spacing={3} justifyContent="center" height="100%" py={1}>
            <Stack spacing={0.5}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 1.2, lineHeight: 1 }}
              >
                {metricCopy.overline}
              </Typography>
              <Typography variant="subtitle1" fontWeight={600}>
                {selected.description || "Select a volume level"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selected.product?.name
                  ? `${selected.product.name} plan`
                  : "Plan"}
              </Typography>
            </Stack>

            <Box px={{ xs: 1, md: 2 }}>
              <Slider
                value={selectedIndex}
                min={0}
                max={Math.max(sortedSubscriptions.length - 1, 0)}
                step={1}
                marks={marks}
                valueLabelDisplay="off"
                onChange={(_, value) => {
                  setSelectedIndex(value as number);
                }}
                sx={{
                  color: "primary.main",
                  height: 8,
                  "& .MuiSlider-rail": {
                    opacity: 0.35,
                    backgroundColor: "primary.light",
                  },
                  "& .MuiSlider-track": {
                    border: "none",
                  },
                  "& .MuiSlider-thumb": {
                    width: 22,
                    height: 22,
                    backgroundColor: "primary.vibrant",
                    "&:hover, &.Mui-focusVisible": {
                      boxShadow: "0 0 0 8px rgba(100, 61, 255, 0.16)",
                    },
                  },
                  "& .MuiSlider-mark": {
                    display: "none",
                  },
                  "& .MuiSlider-markLabel": {
                    top: 32,
                    fontSize: 12,
                    color: "text.secondary",
                  },
                }}
              />
            </Box>

            <Stack direction="row" spacing={1} alignItems="flex-start" pt={2}>
              <Tune fontSize="small" color="disabled" sx={{ mt: 0.25 }} />
              <Typography variant="body2" color="text.secondary">
                {metricCopy.helper}
              </Typography>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </GlassCard>
  );
}

export default SubscriptionVolumePicker;
